// src/offline/usePrefixDownloader.ts
import { useCallback, useEffect } from "react";
import { deleteOfflinePrefix } from "./deletePrefix";
import { addDownloadedPrefix, refreshStorageEstimate, setDownloadStats, clearDownloadStats } from "./offlineMeta";
import { DownloadController, type Prefix, clearDownloadProgress } from "./offlineDownload";
import { downloadQueue } from "./downloadQueue";
import { useOfflineDownloadStore } from "./offlineDownloadStore";
import { useToastStore } from "@/ui/toastStore";

export function usePrefixDownloader(prefix: Prefix) {
  const { jobs, setJob } = useOfflineDownloadStore();
  const job = jobs[prefix];
  const pushToast = useToastStore((s) => s.push);

  // Ensure job record exists; avoid setState during render
  useEffect(() => {
    if (!job) {
      useOfflineDownloadStore.getState().ensureJob(prefix);
    }
  }, [job, prefix]);

  const start = useCallback(async () => {
    if (job?.status === "downloading" || job?.status === "queued") return;

    let lastDownloadedCount = job?.progress?.addressesDownloaded ?? 0;
    const controller = new DownloadController();
    setJob(prefix, {
      controller,
      status: "queued",
      error: null,
    });

    try {
      const result = await downloadQueue.add(prefix, {
        controller,
        onProgress: (progress) => {
          lastDownloadedCount = progress.addressesDownloaded ?? lastDownloadedCount;
          setJob(prefix, { progress });
        },
      });

      if (!result.ok) {
        if (result.cancelled) {
          try {
            await deleteOfflinePrefix(prefix);
            await clearDownloadProgress(prefix);
            await clearDownloadStats(prefix);
            await refreshStorageEstimate();
            setJob(prefix, { status: "cancelled", progress: null, isDownloaded: false });
          } catch (err: any) {
            setJob(prefix, { status: "cancelled", progress: null, error: err?.message });
            pushToast(`Failed to clean up cancelled download: ${err?.message ?? "Unknown error"}`, "error");
          }
        } else {
          const isOfflinePause = result.paused || result.error === "offline";
          setJob(prefix, {
            status: "can_resume",
            error: isOfflinePause ? null : result.error,
          });
          if (!isOfflinePause) {
            pushToast(`Download ${prefix} failed: ${result.error ?? "Unknown error"}`, "error");
          }
        }
        return;
      }

      await addDownloadedPrefix(prefix);
      await setDownloadStats(prefix, {
        addressesDownloaded: lastDownloadedCount,
        updatedAt: new Date().toISOString(),
      });
      await refreshStorageEstimate();
      setJob(prefix, {
        status: "done",
        progress: null,
        isDownloaded: true,
      });
      pushToast(`Download ${prefix} completed`, "success");
    } catch (err: any) {
      setJob(prefix, {
        status: "can_resume",
        error: err.message,
      });
      pushToast(`Download ${prefix} failed: ${err?.message ?? "Unknown error"}`, "error");
    }
  }, [prefix, job, setJob]);

  const cancel = useCallback(async () => {
    if (job?.status === "cancelling") return;
    setJob(prefix, { status: "cancelling" });
    job?.controller?.cancel();
    // Clear progress immediately so a refresh doesn't offer resume while cancelling.
    try {
      await clearDownloadProgress(prefix);
      await clearDownloadStats(prefix);
    } catch (err: any) {
      setJob(prefix, { status: "cancelling", error: err?.message });
      pushToast(`Failed to clear saved progress: ${err?.message ?? "Unknown error"}`, "error");
    }
  }, [prefix, job, setJob, pushToast]);

  const deleteData = useCallback(async () => {
    setJob(prefix, { status: "deleting" });
    try {
      await deleteOfflinePrefix(prefix);
      await clearDownloadStats(prefix);
      await refreshStorageEstimate();
      setJob(prefix, {
        status: "delete_done",
        isDownloaded: false,
      });
    } catch (err: any) {
      setJob(prefix, {
        status: "delete_error",
        error: err.message,
      });
    }
  }, [prefix, setJob]);

  return {
    job,
    // convenience fields for UI components
    status: job?.status ?? "idle",
    isDownloaded: job?.isDownloaded ?? false,
    progress: job?.progress ?? null,
    error: job?.error ?? null,
    startDownload: start,
    cancelDownload: cancel,
    startDelete: deleteData,
    // raw actions
    start,
    cancel,
    deleteData,
  };
}


// // src/offline/usePrefixDownloader.ts
// import { useCallback, useRef } from "react";
// import { downloadPrefix, DownloadController, type Prefix } from "./offlineDownload";
// // import { deleteOfflinePrefix } from "./deleteOfflinePrefix";
// // import { useDownloadJobs } from "./useDownloadJobs";

// export function usePrefixDownloader(prefix: Prefix) {
//   const { jobs, setJob, resetJob } = useDownloadJobs();
//   const controllerRef = useRef<DownloadController | null>(null);

//   const job = jobs[prefix];

//   // ----------------------------
//   // START or RESUME DOWNLOAD
//   // ----------------------------
//   const start = useCallback(async () => {
//     if (job.status === "downloading") return;

//     // Prepare controller
//     const controller = new DownloadController();
//     controllerRef.current = controller;

//     setJob(prefix, {
//       status: job.status === "can_resume" ? "downloading" : "starting",
//       error: null,
//     });

//     try {
//       const result = await downloadPrefix(prefix, {
//         controller,
//         onProgress: (p) =>
//           setJob(prefix, {
//             status: "downloading",
//             progress: p,
//           }),
//       });

//       if (!result.ok) {
//         if (result.cancelled) {
//           setJob(prefix, { status: "idle", progress: undefined });
//         } else {
//           setJob(prefix, {
//             status: "can_resume",
//             error: result.error,
//           });
//         }
//         return;
//       }

//       // Success
//       setJob(prefix, { status: "done", progress: undefined });
//     } catch (err: any) {
//       setJob(prefix, {
//         status: "can_resume",
//         error: err.message,
//       });
//     }
//   }, [prefix, job.status, setJob]);

//   // ----------------------------
//   // CANCEL DOWNLOAD
//   // ----------------------------
//   const cancel = useCallback(() => {
//     controllerRef.current?.cancel();
//     setJob(prefix, { status: "idle" });
//   }, [prefix, setJob]);

//   // ----------------------------
//   // DELETE OFFLINE DATA
//   // ----------------------------
//   const deleteData = useCallback(async () => {
//     setJob(prefix, { status: "deleting" });

//     try {
//       await deleteOfflinePrefix(prefix);
//       resetJob(prefix);
//       setJob(prefix, { status: "delete_done" });
//     } catch (err: any) {
//       setJob(prefix, {
//         status: "delete_error",
//         error: err.message,
//       });
//     }
//   }, [prefix, setJob, resetJob]);

//   return {
//     job,
//     start,
//     cancel,
//     deleteData,
//   };
// }




// // // src/offline/usePrefixDownloader.ts
// // import { useEffect } from "react";
// // import { useOfflineDownloadStore } from "./offlineDownloadStore";
// // // import { downloadQueue } from "./downloadQueue";
// // import { DownloadController, type DownloadResult } from "./offlineDownload";
// // import { addDownloadedPrefix, removeDownloadedPrefix } from "./offlineMeta";
// // import { deleteOfflinePrefix } from "./deletePrefix";
// // import { downloadQueue } from "./downloadQueue";

// // export function usePrefixDownloader(prefix: string) {
// //   const job = useOfflineDownloadStore((s) => s.jobs[prefix]);
// //   const setJob = useOfflineDownloadStore((s) => s.setJob);
// //   const ensureJob = useOfflineDownloadStore((s) => s.ensureJob);

// //   // Ensure job exists on mount
// //   useEffect(() => {
// //     ensureJob(prefix);
// //   }, [prefix]);

// //   // -----------------------
// //   // START DOWNLOAD
// //   // -----------------------
// //   async function startDownload() {
// //     if (job?.status === "downloading" || job?.status === "queued" || job?.status === "starting") return;

// //     const controller = new DownloadController();

// //     setJob(prefix, { status: "queued", controller, error: null });

// //     const result: DownloadResult = await downloadQueue.add(prefix, {
// //       controller,
// //       onProgress: () => {},
// //     });

// //     if (!result.ok) {
// //       setJob(prefix, { status: "error", error: result.error });
// //       return;
// //     }

// //     await addDownloadedPrefix(prefix);
// //     setJob(prefix, { status: "done", isDownloaded: true });
// //   }

// //   // -----------------------
// //   // CANCEL DOWNLOAD
// //   // -----------------------
// //   function cancelDownload() {
// //     job?.controller?.cancel();
// //     setJob(prefix, { status: "cancelled" });
// //   }

// //   // -----------------------
// //   // DELETE
// //   // -----------------------
// //   async function startDelete() {
// //     setJob(prefix, { status: "deleting", error: null });

// //     try {
// //       await deleteOfflinePrefix(prefix);
// //       await removeDownloadedPrefix(prefix);

// //       setJob(prefix, {
// //         status: "delete_done",
// //         isDownloaded: false,
// //         controller: null,
// //         progress: null,
// //       });
// //     } catch (err: any) {
// //       setJob(prefix, {
// //         status: "delete_error",
// //         error: err?.message ?? String(err),
// //       });
// //     }
// //   }

// //   return {
// //     status: job?.status ?? "idle",
// //     isDownloaded: job?.isDownloaded ?? false,
// //     progress: job?.progress ?? null,
// //     error: job?.error ?? null,
// //     startDownload,
// //     cancelDownload,
// //     startDelete,
// //   };
// // }
